import React, { useState, useEffect } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from '../Footer';
import type { NotificationLink } from './NotificationBell';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onPageChange: (page: string) => void;
  isFocusMode?: boolean;
  onNavigate?: (link: NotificationLink) => void;
}

const Layout: React.FC<LayoutProps> = ({ children, currentPage, onPageChange, isFocusMode: focusModeOverride, onNavigate }) => {
  const DESKTOP_QUERY = '(min-width: 769px)';
  const readCollapsedPref = () => {
    try {
      return localStorage.getItem('sidebarCollapsed') !== 'false';
    } catch {
      return true;
    }
  };

  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(readCollapsedPref);

  // 브레이크포인트를 넘을 때만 상태를 바꿉니다.
  // (예전에는 모든 resize에서 초기화돼, 모바일 주소창이 접힐 때 드로어가 닫히고
  //  데스크톱에서 펼친 사이드바가 다시 접히는 문제가 있었습니다.)
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
      setIsSidebarOpen(event.matches);
    };
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  const handleMenuToggle = () => {
    if (isDesktop) {
      const next = !isSidebarCollapsed;
      setIsSidebarOpen(true);
      setIsSidebarCollapsed(next);
      try {
        localStorage.setItem('sidebarCollapsed', String(next));
      } catch {
        // 저장소를 쓸 수 없어도 토글은 동작합니다.
      }
    } else {
      setIsSidebarOpen((open) => !open);
    }
  };

  const isFocusMode = focusModeOverride ?? currentPage === 'exam';

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className={`layout ${isFocusMode ? 'layout--focus' : ''}`}>
      {!isFocusMode && (
        <Header
          onMenuToggle={handleMenuToggle}
          isSidebarOpen={isSidebarOpen}
          isSidebarCollapsed={isDesktop && isSidebarCollapsed}
          isDesktop={isDesktop}
          currentPage={currentPage}
          onPageChange={onPageChange}
          onNavigate={onNavigate}
        />
      )}
      
      {!isFocusMode && (
        <Sidebar
          isOpen={isSidebarOpen}
          isCollapsed={isDesktop && isSidebarCollapsed}
          currentPage={currentPage}
          onPageChange={onPageChange}
          onClose={handleSidebarClose}
        />
      )}
      
      <main className={`layout__main ${!isFocusMode && isSidebarOpen ? (isSidebarCollapsed ? 'layout__main--sidebar-collapsed' : 'layout__main--sidebar-open') : ''}`}>
        <div className="layout__content">
          {children}
        </div>
        {!isFocusMode && <Footer />}
      </main>
    </div>
  );
};

export default Layout;
